import { ref } from 'vue'
import { useRouter } from 'vue-router'

const visible = ref(false)
const message = ref('')
/** @type {import('vue').Ref<'companies' | 'clients' | 'services' | 'appointments' | 'subscription'>} */
const variant = ref('clients')

// Plan-gate state — populated by 402 PLAN_UPGRADE_REQUIRED responses
const planGateFeature = ref('')
const planGateRequiredPlan = ref('')
const planGateCurrentPlan = ref('')
const planGateUpgradeUrl = ref('')

const DEFAULT_MESSAGES = {
  companies: 'Has alcanzado el límite de empresas de tu plan.',
  clients: 'Has alcanzado el límite de clientes de tu plan.',
  services: 'Has alcanzado el límite de servicios de tu plan.',
  appointments: 'Has alcanzado el límite de citas de tu plan.'
}

/**
 * Detecta errores de plan (403 con upgrade_required, o 402 PLAN_UPGRADE_REQUIRED).
 * @param {unknown} err
 * @returns {boolean}
 */
export function isPlanUpgradeRequiredError(err) {
  if (!err || typeof err !== 'object') return false
  if (err.upgradeRequired === true) return true
  // 402 = plan feature gate (new backend contract)
  if (err.status === 402) return true
  // 403 = legacy plan-limit errors (clients, services, etc.)
  if (err.status !== 403) return false
  return err.data?.upgrade_required === true
}

/**
 * Triggers the upgrade modal for plan-gated feature access (HTTP 402).
 * Accepts the full response body from the backend.
 * @param {{ message?: string, feature?: string, requiredPlan?: string, currentPlan?: string, upgradeUrl?: string }} data
 */
export function triggerPlanGate(data) {
  const payload = data && typeof data === 'object' ? data : {}
  planGateFeature.value = payload.feature || ''
  planGateRequiredPlan.value = payload.requiredPlan || ''
  planGateCurrentPlan.value = payload.currentPlan || ''
  planGateUpgradeUrl.value = payload.upgradeUrl || ''
  variant.value = 'subscription'
  message.value = payload.message || 'Tu plan actual no incluye acceso a esta función.'
  visible.value = true
}

/**
 * Triggers the upgrade modal for subscription expiry.
 * Accepts either a legacy string message or the full response body object from api.js.
 * @param {string | object} apiMessageOrData
 */
export function triggerUpgradeRequired(apiMessageOrData) {
  // Handle full data objects from the updated api.js callback
  if (apiMessageOrData && typeof apiMessageOrData === 'object') {
    triggerPlanGate(apiMessageOrData)
    return
  }
  // Legacy: plain string message (backward compatibility)
  planGateFeature.value = ''
  planGateRequiredPlan.value = ''
  planGateCurrentPlan.value = ''
  planGateUpgradeUrl.value = ''
  variant.value = 'subscription'
  message.value =
    typeof apiMessageOrData === 'string' && apiMessageOrData.trim()
      ? apiMessageOrData.trim()
      : 'Tu suscripción ha expirado. Renueva para seguir usando todas las funciones.'
  visible.value = true
}

/** @deprecated usar isPlanUpgradeRequiredError */
export const isClientPlanLimitError = isPlanUpgradeRequiredError

export function usePlanUpgradeModal() {
  const router = useRouter()

  /**
   * @param {string} apiMessage
   * @param {'companies'|'clients'|'services'|'appointments'} variantKey
   */
  function openPlanUpgrade(apiMessage, variantKey = 'clients') {
    planGateFeature.value = ''
    planGateRequiredPlan.value = ''
    planGateCurrentPlan.value = ''
    planGateUpgradeUrl.value = ''
    variant.value = variantKey
    const fallback = DEFAULT_MESSAGES[variantKey] || DEFAULT_MESSAGES.clients
    message.value =
      typeof apiMessage === 'string' && apiMessage.trim()
        ? apiMessage.trim()
        : fallback
    visible.value = true
  }

  /**
   * @param {'companies'|'clients'|'services'|'appointments'} variantKey
   * @returns {boolean} true si se abrió el modal
   */
  function openFromPlanUpgradeError(err, variantKey = 'clients') {
    if (!isPlanUpgradeRequiredError(err)) return false
    // 402 plan-gate errors carry rich data — use triggerPlanGate
    if (err.status === 402 || err.upgradeRequired === true) {
      triggerPlanGate(err.data || {})
      return true
    }
    const msg = err.data?.message || err.message || ''
    openPlanUpgrade(msg, variantKey)
    return true
  }

  function openFromClientLimitError(err) {
    return openFromPlanUpgradeError(err, 'clients')
  }

  function close() {
    visible.value = false
  }

  function goToSubscription() {
    const upgradeUrl = planGateUpgradeUrl.value
    close()
    if (upgradeUrl) {
      try {
        const parsed = new URL(upgradeUrl)
        router.push(parsed.pathname + parsed.search)
      } catch {
        // Not a full URL — treat as in-app path
        router.push(upgradeUrl)
      }
    } else {
      router.push('/dashboard/settings/subscription?tab=planes')
    }
  }

  return {
    visible,
    message,
    variant,
    planGateFeature,
    planGateRequiredPlan,
    planGateCurrentPlan,
    openPlanUpgrade,
    openFromPlanUpgradeError,
    openFromClientLimitError,
    close,
    goToSubscription
  }
}
